import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { publicApi, roomsApi } from '../api'
import { ApiError } from '../api/client'
import type { Room as RoomType, RoomMessage } from '../api/types'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useActiveSession, useRoomSessionApi } from '../context/RoomSessionContext'
import type { ChatMessage } from '../webrtc/types'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Dialog } from '../components/ui/Dialog'
import { Spinner } from '../components/ui/Spinner'
import { VideoTile } from '../components/VideoTile'
import { ChatPanel } from '../components/ChatPanel'
import { DevicePicker } from '../components/DevicePicker'
import { MembersPanel } from '../components/MembersPanel'

type Layout = 'grid' | 'speaker' | 'pip'

export function RoomPage() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { user } = useAuth()
  const { active, enterSession, leaveSession } = useRoomSessionApi()
  const activeSession = useActiveSession()

  const [room, setRoom] = useState<RoomType | null>(active && active.slug === slug ? active.room : null)
  const [joining, setJoining] = useState(!activeSession || activeSession.session.slug !== slug)
  const [password, setPassword] = useState('')
  const [needsPassword, setNeedsPassword] = useState(false)
  const [switchPrompt, setSwitchPrompt] = useState<RoomType | null>(null)

  const [layoutMode, setLayoutMode] = useState<Layout>('grid')
  const [chatOpen, setChatOpen] = useState(false)
  const [deviceOpen, setDeviceOpen] = useState(false)
  const [membersOpen, setMembersOpen] = useState(false)
  const [pinnedPeerId, setPinnedPeerId] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const isRoomAdmin = !!user && !!room && (room.ownerId === user.id || user.role === 'ADMIN')

  // Load room metadata + ticket once. If a session for THIS slug is already
  // active (user navigated back to the room), skip the join flow entirely.
  useEffect(() => {
    if (activeSession && activeSession.session.slug === slug) {
      setRoom(activeSession.session.room)
      setJoining(false)
      return
    }
    void (async () => {
      try {
        const [publicCfg, roomInfo] = await Promise.all([
          publicApi.config(),
          roomsApi.get(slug),
        ])
        setRoom(roomInfo.room)
        // If a different session is active, ask before switching.
        if (active && active.slug !== slug) {
          setSwitchPrompt(roomInfo.room)
          setJoining(false)
          return
        }
        if (roomInfo.room.requiresPassword) {
          setNeedsPassword(true)
          setJoining(false)
        } else {
          await attemptJoin(undefined, publicCfg.iceServers)
        }
      } catch {
        toast.push('Raum nicht gefunden', 'error')
        navigate('/', { replace: true })
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  async function attemptJoin(pw?: string, iceServersOverride?: RTCIceServer[]) {
    setJoining(true)
    try {
      const iceServers =
        iceServersOverride ?? (await publicApi.config()).iceServers
      const res = await roomsApi.join(slug, pw)
      // Pre-fetch chat history so the mesh hook seeds with it from the start.
      let initialMessages: ChatMessage[] | undefined
      try {
        const { messages } = await roomsApi.messages(slug)
        const selfUserId = user?.id
        initialMessages = messages.map((m: RoomMessage) => ({
          id: m.id,
          from: '',
          displayName: m.displayName,
          text: m.text,
          ts: m.ts,
          self: !!selfUserId && m.userId === selfUserId,
        }))
      } catch {
        /* not allowed or no history yet — leave undefined */
      }
      enterSession({
        slug,
        room: res.room,
        ticket: res.ticket,
        iceServers,
        initialMessages,
      })
      setRoom(res.room)
      setNeedsPassword(false)
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setNeedsPassword(true)
        toast.push('Passwort ungültig', 'error')
      } else {
        toast.push('Beitritt fehlgeschlagen', 'error')
        navigate('/', { replace: true })
      }
    } finally {
      setJoining(false)
    }
  }

  async function clearChatHistory() {
    if (!room || !activeSession) return
    try {
      await roomsApi.clearMessages(room.id)
      activeSession.mesh.clearChatLocally()
      toast.push('Chatverlauf gelöscht', 'success')
    } catch {
      toast.push('Löschen fehlgeschlagen', 'error')
    }
  }

  // Send presence updates whenever local media state changes.
  useEffect(() => {
    if (!activeSession) return
    activeSession.mesh.sendPresence({
      audio: activeSession.media.audioEnabled,
      video: activeSession.media.videoEnabled,
      screen: activeSession.media.screenSharing,
    })
  }, [
    activeSession,
    activeSession?.media.audioEnabled,
    activeSession?.media.videoEnabled,
    activeSession?.media.screenSharing,
  ])

  const allTiles = useMemo(() => {
    if (!activeSession) return []
    const { media, mesh } = activeSession
    return [
      {
        id: 'self',
        self: true,
        stream: media.stream,
        name: user?.displayName ?? 'Ich',
        audio: media.audioEnabled,
        video: media.videoEnabled,
        screen: media.screenSharing,
      },
      ...mesh.peers.map((p) => ({
        id: p.info.id,
        self: false,
        stream: p.stream,
        name: p.info.displayName || 'Peer',
        audio: p.info.audio,
        video: p.info.video,
        screen: p.info.screen,
        connectionState: p.connectionState,
      })),
    ] as Array<{
      id: string
      self: boolean
      stream: MediaStream | null
      name: string
      audio: boolean
      video: boolean
      screen: boolean
      connectionState?: RTCPeerConnectionState
    }>
  }, [activeSession, user])

  const focusedId = useMemo(() => {
    if (pinnedPeerId && allTiles.some((t) => t.id === pinnedPeerId)) return pinnedPeerId
    const screener = allTiles.find((t) => !t.self && t.screen)
    if (screener) return screener.id
    const firstRemote = allTiles.find((t) => !t.self)
    return firstRemote?.id ?? 'self'
  }, [pinnedPeerId, allTiles])

  useEffect(() => {
    function onFsChange() {
      const fsEl = document.fullscreenElement ?? (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement
      setIsFullscreen(!!fsEl)
    }
    document.addEventListener('fullscreenchange', onFsChange)
    document.addEventListener('webkitfullscreenchange', onFsChange)
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange)
      document.removeEventListener('webkitfullscreenchange', onFsChange)
    }
  }, [])

  async function toggleFullscreen() {
    const el = rootRef.current
    if (!el) return
    const doc = document as Document & {
      webkitFullscreenElement?: Element
      webkitExitFullscreen?: () => void
    }
    const elExt = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }
    const activeFs = doc.fullscreenElement ?? doc.webkitFullscreenElement
    if (activeFs) {
      if (document.exitFullscreen) await document.exitFullscreen()
      else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen()
    } else if (el.requestFullscreen) {
      await el.requestFullscreen()
    } else if (elExt.webkitRequestFullscreen) {
      await elExt.webkitRequestFullscreen()
    } else {
      el.classList.toggle('pseudo-fullscreen')
      setIsFullscreen((v) => !v)
    }
  }

  function leave() {
    leaveSession()
    navigate('/')
  }

  if (joining && !needsPassword && !switchPrompt) return <Spinner fullscreen />

  if (switchPrompt) {
    return (
      <Dialog
        open
        onClose={() => navigate('/')}
        title="In anderen Raum wechseln?"
        footer={
          <>
            <Button variant="ghost" onClick={() => navigate('/')}>
              Abbrechen
            </Button>
            <Button
              onClick={async () => {
                leaveSession()
                setSwitchPrompt(null)
                if (switchPrompt.requiresPassword) {
                  setNeedsPassword(true)
                } else {
                  await attemptJoin()
                }
              }}
            >
              Wechseln
            </Button>
          </>
        }
      >
        <p>
          Du bist gerade in einem anderen Raum. Möchtest du diesen verlassen und „
          {switchPrompt.name}" beitreten?
        </p>
      </Dialog>
    )
  }

  if (needsPassword && (!activeSession || activeSession.session.slug !== slug)) {
    return (
      <Dialog
        open
        onClose={() => navigate('/')}
        title={`Passwort für „${room?.name ?? slug}" erforderlich`}
        footer={
          <>
            <Button variant="ghost" onClick={() => navigate('/')}>
              Abbrechen
            </Button>
            <Button onClick={() => void attemptJoin(password)} loading={joining}>
              Beitreten
            </Button>
          </>
        }
      >
        <Input
          type="password"
          autoFocus
          label="Raum-Passwort"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void attemptJoin(password)
          }}
        />
      </Dialog>
    )
  }

  if (!activeSession) return <Spinner fullscreen />

  const { media, mesh } = activeSession
  const focused = allTiles.find((t) => t.id === focusedId) ?? allTiles[0]
  const others = allTiles.filter((t) => t.id !== focused.id)

  return (
    <div className={`room layout-${layoutMode}`} ref={rootRef}>
      <header className="room-head">
        <div>
          <h2>{room?.name ?? slug}</h2>
          <span className={`conn ${mesh.connected ? 'on' : 'off'}`}>
            {mesh.connected ? 'Verbunden' : 'Verbinde…'}
          </span>
        </div>
      </header>

      <div className="room-body">
        <div className={`stage ${chatOpen ? 'with-chat' : ''}`}>
          {layoutMode === 'grid' && (
            <div className="grid" style={gridStyle(allTiles.length)}>
              {allTiles.map((t) => (
                <VideoTile
                  key={t.id}
                  stream={t.stream}
                  displayName={t.self ? `${t.name} (Ich)` : t.name}
                  muted={t.self}
                  mirrored={t.self && !t.screen}
                  audioEnabled={t.audio}
                  videoEnabled={t.video}
                  screen={t.screen}
                  connectionState={t.connectionState}
                  audioOutputId={media.selectedAudioOut}
                  onClick={() => setPinnedPeerId(t.id === pinnedPeerId ? null : t.id)}
                  pinned={t.id === pinnedPeerId}
                />
              ))}
            </div>
          )}

          {layoutMode === 'speaker' && focused && (
            <div className="speaker">
              <VideoTile
                stream={focused.stream}
                displayName={focused.self ? `${focused.name} (Ich)` : focused.name}
                muted={focused.self}
                mirrored={focused.self && !focused.screen}
                audioEnabled={focused.audio}
                videoEnabled={focused.video}
                screen={focused.screen}
                connectionState={focused.connectionState}
                audioOutputId={media.selectedAudioOut}
                focused
              />
              <div className="strip">
                {others.map((t) => (
                  <VideoTile
                    key={t.id}
                    stream={t.stream}
                    displayName={t.self ? `${t.name} (Ich)` : t.name}
                    muted={t.self}
                    mirrored={t.self && !t.screen}
                    audioEnabled={t.audio}
                    videoEnabled={t.video}
                    screen={t.screen}
                    connectionState={t.connectionState}
                    audioOutputId={media.selectedAudioOut}
                    onClick={() => setPinnedPeerId(t.id)}
                    pinned={t.id === pinnedPeerId}
                  />
                ))}
              </div>
            </div>
          )}

          {layoutMode === 'pip' && focused && (
            <div className="pip">
              <VideoTile
                stream={focused.stream}
                displayName={focused.self ? `${focused.name} (Ich)` : focused.name}
                muted={focused.self}
                mirrored={focused.self && !focused.screen}
                audioEnabled={focused.audio}
                videoEnabled={focused.video}
                screen={focused.screen}
                connectionState={focused.connectionState}
                audioOutputId={media.selectedAudioOut}
                focused
              />
              {others.slice(0, 1).map((t) => (
                <div className="pip-overlay" key={t.id}>
                  <VideoTile
                    stream={t.stream}
                    displayName={t.self ? `${t.name} (Ich)` : t.name}
                    muted={t.self}
                    mirrored={t.self && !t.screen}
                    audioEnabled={t.audio}
                    videoEnabled={t.video}
                    screen={t.screen}
                    connectionState={t.connectionState}
                    audioOutputId={media.selectedAudioOut}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {chatOpen && room?.chatEnabled ? (
          <ChatPanel
            messages={mesh.chat}
            onSend={mesh.sendChat}
            onClose={() => setChatOpen(false)}
            canClear={isRoomAdmin}
            onClear={() => void clearChatHistory()}
          />
        ) : null}

        {membersOpen && room ? (
          <MembersPanel
            roomId={room.id}
            onClose={() => setMembersOpen(false)}
          />
        ) : null}
      </div>

      <footer className="controls">
        <Button
          variant={media.audioEnabled ? 'secondary' : 'danger'}
          onClick={media.toggleAudio}
          iconOnly
          aria-label={media.audioEnabled ? 'Mikrofon stumm' : 'Mikrofon an'}
        >
          {media.audioEnabled ? '🎙️' : '🔇'}
        </Button>
        <Button
          variant={media.videoEnabled ? 'secondary' : 'danger'}
          onClick={media.toggleVideo}
          iconOnly
          aria-label={media.videoEnabled ? 'Kamera aus' : 'Kamera an'}
        >
          {media.videoEnabled ? '📷' : '🚫'}
        </Button>
        {room?.screenShareEnabled && media.screenShareSupported ? (
          <Button
            variant={media.screenSharing ? 'primary' : 'secondary'}
            onClick={() =>
              media.screenSharing ? void media.stopScreenShare() : void media.startScreenShare()
            }
            aria-label={media.screenSharing ? 'Freigabe beenden' : 'Bildschirm teilen'}
          >
            <span className="btn-ic" aria-hidden>📺</span>
            <span className="btn-lb">{media.screenSharing ? 'Freigabe beenden' : 'Bildschirm teilen'}</span>
          </Button>
        ) : null}
        <Button
          variant="ghost"
          iconOnly
          onClick={() => setDeviceOpen(true)}
          aria-label="Geräte"
        >
          ⚙️
        </Button>
        {room?.chatEnabled ? (
          <Button variant="ghost" onClick={() => setChatOpen((v) => !v)} aria-label="Chat">
            <span className="btn-ic" aria-hidden>💬</span>
            <span className="btn-lb">Chat</span>
          </Button>
        ) : null}
        {isRoomAdmin && room?.isPrivate ? (
          <Button variant="ghost" onClick={() => setMembersOpen((v) => !v)} aria-label="Mitglieder">
            <span className="btn-ic" aria-hidden>👥</span>
            <span className="btn-lb">Mitglieder</span>
          </Button>
        ) : null}
        <Button variant="ghost" onClick={() => void toggleFullscreen()} aria-label={isFullscreen ? 'Vollbild verlassen' : 'Vollbild'}>
          <span className="btn-ic" aria-hidden>⛶</span>
          <span className="btn-lb">{isFullscreen ? 'Vollbild verlassen' : 'Vollbild'}</span>
        </Button>
        <div className="controls-spacer" />
        <Button
          size="sm"
          variant={layoutMode === 'grid' ? 'primary' : 'ghost'}
          onClick={() => setLayoutMode('grid')}
          aria-label="Raster"
        >
          <span className="btn-ic" aria-hidden>▦</span>
          <span className="btn-lb">Raster</span>
        </Button>
        <Button
          size="sm"
          variant={layoutMode === 'speaker' ? 'primary' : 'ghost'}
          onClick={() => setLayoutMode('speaker')}
          aria-label="Sprecher"
        >
          <span className="btn-ic" aria-hidden>👤</span>
          <span className="btn-lb">Sprecher</span>
        </Button>
        <Button
          size="sm"
          variant={layoutMode === 'pip' ? 'primary' : 'ghost'}
          onClick={() => setLayoutMode('pip')}
          aria-label="PiP"
        >
          <span className="btn-ic" aria-hidden>▣</span>
          <span className="btn-lb">PiP</span>
        </Button>
        <Button variant="danger" onClick={leave} aria-label="Raum verlassen">
          <span className="btn-ic" aria-hidden>📞</span>
          <span className="btn-lb">Verlassen</span>
        </Button>
      </footer>

      <DevicePicker
        open={deviceOpen}
        onClose={() => setDeviceOpen(false)}
        devices={media.devices}
        selectedAudioIn={media.selectedAudioIn}
        selectedVideoIn={media.selectedVideoIn}
        selectedAudioOut={media.selectedAudioOut}
        quality={media.quality}
        onAudioIn={(id) => void media.switchAudio(id)}
        onVideoIn={(id) => void media.switchVideo(id)}
        onAudioOut={(id) => void media.switchOutput(id)}
        onQuality={(q) => void media.switchQuality(q)}
      />

      {media.error ? (
        <div className="media-error" role="alert">
          Mediengerät-Fehler: {media.error}
        </div>
      ) : null}
    </div>
  )
}

function gridStyle(count: number): CSSProperties {
  const cols = count <= 1 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : 4
  return { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }
}
